import { ApolloError } from '@apollo/client';

/**
 * Some access states are reported by the backend as a GraphQL error in a 200
 * response rather than as data, e.g.
 *
 *   { "errors": [{ "message": "User is disabled", "path": ["availableClients"] }],
 *     "data": null }
 *
 * The errors carry no `extensions.code`, so the message is the only
 * discriminator. Matching is case-insensitive and substring-based so a wrapped
 * prefix or trailing punctuation still resolves — note the no-account message
 * already ends in a period.
 *
 * These strings are backend copy, so a reword silently stops matching and the
 * user falls through to the generic "Oops! Something Went Wrong" error
 * boundary. No frontend test can catch that. Replace both checks with
 * `extensions.code` if the backend ever adds one.
 */
const DEACTIVATED_MESSAGE = /User is disabled/i;
const NO_ACCOUNT_ACCESS_MESSAGE = /You are not a member of this account/i;

const hasMessage = (error: ApolloError | undefined, expected: RegExp) =>
  !!error?.graphQLErrors?.some(({ message }) => expected.test(message));

export const isUserDeactivatedError = (error?: ApolloError): boolean =>
  hasMessage(error, DEACTIVATED_MESSAGE);

export const isNoAccountAccessError = (error?: ApolloError): boolean =>
  hasMessage(error, NO_ACCOUNT_ACCESS_MESSAGE);
